import __ from "lodash";
import {defaultChargeRecord} from "./DefaultChargeRecord";

const getVolumetricWeight = (volume) => {
  return ( volume / 0.006);
}

const getChargeableWeight = (weight, volume) => {
  if (volume) {
    const volumetricWeight = getVolumetricWeight(volume);
    if (!weight)
      return volumetricWeight;
    return (volumetricWeight > weight ? volumetricWeight : weight);
  }
  return 0;
}

const getWeightVolumeRatio = (volume, weight) => {
  if (!weight || !volume)
    return "";
  return volume > 0 ? `${(weight / volume).toFixed(2)}KG : CBM` : "";
}

const getParticipantDetails = (booking, prefix) => {
  const participant = {};
  Object.keys(booking).map(key => {
    if (key.startsWith(prefix)) {
      const newKey = __.camelCase(key.replace(prefix, ""));
      participant[newKey] = booking[key];
    }
  })
  return participant;
}

const forwarderRecord = {
  forwarderContactUuid: "",
  forwarderCompanyName: "",
  forwarderIsNew: false,
  forwarderSalutation: "",
  forwarderFirstName: "",
  forwarderLastName: "",
  forwarderPhone: "",
  forwarderAltPhone: "",
  forwarderEmail: "",
}

const packBookingRecord = (booking) => {

  const forwarderContact = getParticipantDetails(booking, "forwarder");
  const shipperContact = getParticipantDetails(booking, "shipper");
  const consigneeContact = getParticipantDetails(booking, "consignee");

  if (__.has(booking, "viaRouting")) {
    if (__.has(booking.viaRouting, "trips") && Array.isArray(booking.viaRouting.trips) && booking.viaRouting.trips.length > 0)
      booking["tripUuid"] = booking.viaRouting.trips[0];
    if (__.has(booking.viaRouting, "flights") && Array.isArray(booking.viaRouting.flights) && booking.viaRouting.flights.length > 0) {
      const firstTrip = booking.viaRouting.flights[0];
      if (firstTrip && Array.isArray(firstTrip) && firstTrip.length > 0)
        booking["tripLegUuid"] = firstTrip[0].tripLegUuid;
    }
  }
  let dimensionsAvailable = false;
  if (booking.dimensions && Array.isArray(booking.dimensions)) {
    booking.dimensions.forEach(dimension => {
      dimensionsAvailable = true;
      if (dimension.deckRequired === "MAIN")
        booking["deckRequired"] = "MAIN";
    });
  }

  const bookingRecord = {
    ...booking,
    bookingUuid: booking.bookingUuid,
    bookingType: booking.bookingType || "CONTRACT", //   ['CONTRACT', 'SPOT', 'SPECIAL LOAD'],
    bookingStatus: booking.bookingStatus ? (booking.bookingType !== "SPOT" && booking.bookingStatus === "PRE-BOOKING" && dimensionsAvailable ? "BOOKED" : booking.bookingStatus) : (booking.bookingType !== "SPOT" && dimensionsAvailable ? "BOOKED" : "PRE-BOOKING"), //['PRE-ADVICE', 'PRE-BOOKING', 'BOOKED', 'CONFIRMED', 'WAREHOUSE', 'MANIFEST', 'OFFLOADED', 'IN-TRANSIT', 'DELIVERED', 'CANCELLED', 'SPOT', 'FLIGHT-SUMMARY']
    upliftDateUnknown: "",
    awbUuid: booking.awbUuid,
    awbNumber: booking.awbNumber,
    gsaCompanyUuid: booking.gsaCompanyUuid,
    bookedByCompanyContactUuid: booking.gsaContactUuid,
    forwarderCompanyUuid: booking.forwarderCompanyUuid,
    forwarderContact: forwarderContact,
    shipperCompanyUuid: booking.shipperCompanyUuid,
    shipperContact: shipperContact,
    consigneeCompanyUuid: booking.consigneeCompanyUuid,
    consigneeContact: consigneeContact,
    tripUuid: booking.tripUuid,
    tripLegUuid: booking.tripLegUuid,
    requestedDate: booking.flightDate,
    extraOrigin: booking.origin,
    origin: booking.charterOrigin,
    viaRouting: booking.viaRouting || "",
    consolidation: booking.consolidation,
    destination: booking.destination,
    expectedPieces: booking.expectedPieces,
    expectedWeight: booking.expectedWeightKg,
    expectedWeightUom: "KG",
    expectedWeightKg: booking.expectedWeightKg,
    expectedVolume: booking.expectedVolume,
    expectedChargeableWeight: booking.expectedChargeableWeight,
    commodity: booking.commodity,
    specialLoadCode: booking.specialLoad,
    stackableStatus: booking.stackable,
    cratedStatus: booking.crated,
    tiltableStatus: booking.tiltable,
    outSizedStatus: booking.outSized,
    aviStatus: booking.avi,
    deliverySecurity: booking.delivery,
    containsDangerousGoods: booking.dgr,
    caoStatus: booking.dgr === "CAO",
    deckRequired: booking.deckRequired || "LOWER", //['MAIN', 'LOWER']
    priority: "L", //['X', 'H', 'L', 'S']
    prioritySequence: 0,
    allotmentCode: booking.allotmentCode,
    bookingReference: "",
    expectedAvailabilityAtOrigin: booking.originDelivery,
    expectedDeliveryAtDestination: "",
    confirmedAt: "",
    tariffHeaderUuid: "",
    isZeroRate: "",
    warehouseActionsRequired: "",
    specialInfo: booking.specialInformation,
    quantityOfUnNumber: booking.quantityOfUnNumber,
    financeComments: booking.financeComments,
    cargoDescription: booking.cargoDescription,
    note: booking.note,
    loadabilityNotes: booking.loadabilityNotes,
// Spot rates
    spotReference: booking.spotReference || "",
    customerTargetRate: booking.customerTargetRate,
    spotChargeableWeight: booking.spotChargeableWeight,
    offeredSpotRate: booking.offeredSpotRate,
// Arrays
    dims: booking.dimensions,
    charges: booking.rates,
    shcs: booking.shcs,
    unNumbers: booking.unNumbers,
    documents: booking.documents,
  }
  return bookingRecord;
}

const getRates = (baseCurrency, exchangeRates) => {
  const baseRate = __.find(exchangeRates, {currencyCode: baseCurrency}) || 0;
  const usdRate = __.find(exchangeRates, {currencyCode: "USD"}) || 0;
  const gbpRate = __.find(exchangeRates, {currencyCode: "GBP"}) || 0;

  const baseToEur = baseRate.rate;
  const baseToUsd = baseRate.rate * usdRate.inverseRate;
  const baseToGbp = baseRate.rate * gbpRate.inverseRate;

  return {baseToEur, baseToUsd, baseToGbp}
};

const prepareRates = (record) => {
  const rateRecord = { ...defaultChargeRecord, chargeType: "Primary", chargeTypeValue: "Primary", baseRateCurrency: record.currencyCode };
  rateRecord["sequence"] = 1;
  rateRecord["quantity"] = record.unitAmount;
  rateRecord["unitType"] = record.unitType;
  rateRecord["quantityUnits"] = record.unitAmount;
  rateRecord["tariffRate"] = {found: true, rate: record.rate};
  rateRecord["baseRate"] = record.rate;
  rateRecord["baseRateTotal"] = record.amount;
  rateRecord["usdRateTotal"] = record.usdAmount;
  if (__.has(record, "exchangeRates"))
    rateRecord["exchangeRates"] = record.exchangeRates;

  return [rateRecord];
}

const getCurrencyCode = (charges) => {
  if (charges && Array.isArray(charges) && charges.length > 0) {
    const chargeLine = charges[0];
    return chargeLine.baseRateCurrency;
  }
  return "";
}

const prepareRecordForEditing = (record, exchangeRates, currencies) => {
  if (!record.currencyCode)
    record['currencyCode'] = 'EUR';
  let currency = __.find(currencies, {currencyCode: record.currencyCode});
  if (!currency) {
    const currencyCode = getCurrencyCode(record.charges);
    currency = __.find(currencies, {currencyCode: currencyCode});
    if (currency)
      record.currencyCode = currencyCode;
    else
      record.currencyCode = "EUR"; //TODO: throw an error if no currency is found?
  }
  let consolidation = false;
  if (record.dims && Array.isArray(record.dims) & record.dims.length === 0) {
    consolidation = record.expectedPieces > 0 && record.expectedWeightKg > 0 ? true : false;
  }
  let overrideChargeableWeight = false;
  if (record.spotChargeableWeight) {
    const spotChargeableWeight = Number(record.spotChargeableWeight);
    overrideChargeableWeight = spotChargeableWeight > 0 && spotChargeableWeight !== record.expectedChargeableWeight;
  }

  const bookingRecord = {
    ...record,
    dimensions: record.dims,
    rates: record.charges?record.charges:prepareRates(record),
    gsaDefaultCurrencyCode: record.currencyCode,
    gsaDefaultCurrencyName: currency.currencyName || "",
    gsaCurrencyRates: getRates(record.currencyCode, exchangeRates),
    gsaCompanyName: record.companyName,
    gsaCompanyContact: `${record.title ? record.title +  " " : ""} ${record.forename} ${record.surname} ${record.role? '(' + record.role + ')' : ''}`,
    origin: record.extraOrigin ? record.extraOrigin : record.origin,
    charterOrigin: record.origin,
    flightDate: record.requestedDate,
    originDelivery: record.expectedAvailabilityAtOrigin,
    volumetricWeight: getVolumetricWeight(record.expectedVolume),
    ratio: getWeightVolumeRatio(record.expectedVolume, record.expectedWeightKg),
    stackable: record.stackableStatus ? "YES" : record.stackableStatus === 0 ? "NO" : "NONE",
    tiltable: record.tiltableStatus ? "YES" : record.tiltableStatus === 0 ? "NO" : "NONE",
    crated: record.cratedStatus ? "YES" : record.cratedStatus === 0 ? "NO" : "NONE",
    dgr: record.caoStatus ? "CAO" : record.containsDangerousGoods ? "YES" : record.containsDangerousGoods === 0 ? "NO" : "NONE",
    avi: record.aviStatus ? "YES" : record.aviStatus === 0 ? "NO" : "NONE",
    outSized: record.outSizedStatus ? "YES" : record.outSizedStatus === 0 ? "NO" : "NONE",
    delivery: record.deliverySecurity ? "SECURED" : "UNSECURED",
    specialLoad: record.specialLoadCode,
    specialInformation: record.specialInfo,
    quantityOfUnNumber: record.quantityOfUnNumber,
    consolidation,
    spotChargeableWeight: record.spotChargeableWeight ? Number(record.spotChargeableWeight) : null,
    overrideChargeableWeight: overrideChargeableWeight,
    awbSerial: record.awbSerial?.toString().padStart(8, "0"),
  }
  if (__.has(record, "viaRouting")) {
    if (__.has(record.viaRouting, "departureFlight"))
      bookingRecord["departureFlight"] = record.viaRouting.departureFlight;
  }
  if (__.has(bookingRecord, "unNumbers") && typeof bookingRecord.unNumbers === "string") {
    bookingRecord.unNumbers = JSON.parse(bookingRecord.unNumbers);
  }

  return bookingRecord;
}


const unpackBookingRecord = (booking) => {

  const forwarderContact = getParticipantDetails(booking, "forwarder");
  const shipperContact = getParticipantDetails(booking, "shipper");
  const consigneeContact = getParticipantDetails(booking, "consignee");

  const bookingRecord = {
    bookingType: "", //   ['CONTRACT', 'SPOT', 'SPECIAL LOAD'],
    spotReference: "",
    upliftDateUnknown: "",
    awbUuid: "",
    awbNumber: booking.awbNumber,
    gsaCompanyUuid: booking.gsaCompanyUuid,
    bookedByCompanyContactUuid: booking.gsaContactUuid,
    forwarderCompanyUuid: booking.forwarderCompanyUuid,
    forwarderContact: forwarderContact,
    shipperCompanyUuid: booking.shipperCompanyUuid,
    shipperContact: shipperContact,
    consigneeCompanyUuid: booking.consigneeCompanyUuid,
    consigneeContact: consigneeContact,
    tripUuid: "",
    requestedDate: booking.flightDate,
    extraOrigin: booking.origin,
    origin: booking.charterOrigin,
    viaRouting: "",
    consolidation: booking.consolidation,
    destination: booking.destination,
    expectedPieces: booking.expectedPieces,
    expectedWeight: booking.expectedWeightKg,
    expectedWeightUom: "KG",
    expectedWeightKg: booking.expectedWeightKg,
    expectedVolume: booking.expectedVolume,
    expectedChargeableWeight: booking.expectedChargeableWeight,
    commodity: booking.commodity,
    specialLoadCode: booking.specialLoad,
    stackableStatus: booking.stackable,
    cratedStatus: booking.crated,
    tiltableStatus: booking.tiltable,
    outSizedStatus: booking.outSized,
    aviStatus: booking.avi,
    deliverySecurity: booking.delivery,
    bookingStatus: "PRE-BOOKING", //['PRE-ADVICE', 'PRE-BOOKING', 'BOOKED', 'CONFIRMED', 'WAREHOUSE', 'MANIFEST', 'OFFLOADED', 'IN-TRANSIT', 'DELIVERED', 'CANCELLED', 'SPOT', 'FLIGHT-SUMMARY']
    containsDangerousGoods: booking.dgr,
    caoStatus: booking.caoStatus,
    deckRequired: "", //['MAIN', 'LOWER']
    priority: "L", //['X', 'H', 'L', 'S']
    prioritySequence: 0,
    allotmentCode: "",
    bookingReference: "",
    expectedAvailabilityAtOrigin: booking.originDelivery,
    expectedDeliveryAtDestination: "",
    confirmedAt: "",
    tariffHeaderUuid: "",
    isZeroRate: "",
    warehouseActionsRequired: "",
    specialInfo: booking.specialInformation,
    financeComments: booking.financeComments,
    cargoDescription: booking.cargoDescription,
    note: booking.note,
    dims: booking.dimensions,
    charges: booking.rates,
    shcs: booking.shcs,
    documents: booking.documents,
  }

  return bookingRecord;
}

const setConfirmedSpotRate = (bookingRecord, exchangeRates) => {
  let charges = bookingRecord.charges;
  if (charges && Array.isArray(charges)) {
    const chargeableWeight = bookingRecord.spotChargeableWeight && Number(bookingRecord.spotChargeableWeight) > 0 ? Number(bookingRecord.spotChargeableWeight) : bookingRecord.expectedChargeableWeight;
    const spotRate = bookingRecord.offeredSpotRate && Number(bookingRecord.offeredSpotRate) > 0 ? Number(bookingRecord.offeredSpotRate) : bookingRecord.customerTargetRate;
    const {baseToEur, baseToUsd, baseToGbp} = getRates(bookingRecord.currencyCode, exchangeRates)
    let eurTotalAmount = 0;
    let gbpTotalAmount = 0;
    let usdTotalAmount = 0;

    if (charges.length > 0) {
      charges.forEach(charge => {
        if (charge.chargeTypeValue === "Freight Charge" && charge.chargeGroupCode === "CARGO" && charge.unitType === "CHARGEABLE_WEIGHT") {
          charge["baseRate"] = spotRate;
          charge["quantityUnits"] = chargeableWeight;
          charge["baseRateTotal"] = spotRate * chargeableWeight;
          charge["usdRateTotal"] = spotRate * chargeableWeight * baseToUsd;
        }
      });
    } else {
      charges = prepareRates(
        {
          currencyCode: bookingRecord.currencyCode,
          unitAmount: chargeableWeight,
          unitType: "CHARGEABLE_WEIGHT",
          quantityUnits: chargeableWeight,
          rate: spotRate,
          baseRate: spotRate,
          amount: spotRate * chargeableWeight,
          usdAmount: spotRate * chargeableWeight * baseToUsd,
          exchangeRates: {baseToEur, baseToGbp, baseToUsd}
        }
      );
    }

    charges.forEach(charge=> {
      eurTotalAmount += charge.baseRateTotal * baseToEur;
      gbpTotalAmount += charge.baseRateTotal * baseToGbp;
      usdTotalAmount += charge.baseRateTotal * baseToUsd;
    });

    // console.log(charges, eurTotalAmount, gbpTotalAmount, usdTotalAmount);
    return {charges, eurTotalAmount, gbpTotalAmount, usdTotalAmount}
  }
}


export {
  prepareRecordForEditing,
  packBookingRecord,
  getVolumetricWeight,
  getChargeableWeight,
  getWeightVolumeRatio,
  forwarderRecord,
  setConfirmedSpotRate,
}
